import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1e293b'
  },
  headerBanner: {
    backgroundColor: '#0c4a6e',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#7dd3fc',
    marginTop: 2
  },
  badge: {
    backgroundColor: '#0284c7',
    color: '#ffffff',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 7.5,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#0369a1',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.2,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 6.8,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 6.8,
    color: '#0f172a',
    width: '55%'
  },
  passText: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 5.8,
    color: '#64748b'
  }
});

const CentralHeatingPowerflushCertificatePDF = ({ formData = {} }) => {
  const contractor = formData.contractorDetails || {};
  const property = formData.propertyDetails || {};
  const sys = formData.systemOverview || {};
  const pre = formData.preFlushAssessment || {};
  const flush = formData.flushingProcess || {};
  const post = formData.postFlushVerification || {};
  const dec = formData.declarations || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Banner */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Central Heating Powerflush & Water Treatment</Text>
            <Text style={styles.headerSubtitle}>BS 7593:2019 Code of Practice Compliance Certificate</Text>
          </View>
          <View style={styles.badge}>
            <Text>BS 7593 COMPLIANT</Text>
          </View>
        </View>

        {/* 1 & 2: Contractor & Property */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. Heating Contractor</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Company:</Text>
              <Text style={styles.fieldValue}>{contractor.companyName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Engineer:</Text>
              <Text style={styles.fieldValue}>{contractor.engineerName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Telephone:</Text>
              <Text style={styles.fieldValue}>{contractor.telephone || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Accreditation No:</Text>
              <Text style={styles.fieldValue}>{contractor.accreditationNumber || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Client & Property</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Client Name:</Text>
              <Text style={styles.fieldValue}>{property.clientName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{property.propertyAddress || 'N/A'}, {property.postCode || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date of Flush:</Text>
              <Text style={styles.fieldValue}>{property.flushDate || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Reason for Flush:</Text>
              <Text style={styles.fieldValue}>{sys.reasonForFlush || 'Benchmark Scheme'}</Text>
            </View>
          </View>
        </View>

        {/* 3: System Overview */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>3. Central Heating System Specification</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>System Architecture:</Text>
                <Text style={styles.fieldValue}>{sys.systemType || 'Sealed System'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Boiler / Heat Source:</Text>
                <Text style={styles.fieldValue}>{sys.boilerMakeModel || 'N/A'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Radiators / Emitters:</Text>
                <Text style={styles.fieldValue}>{sys.radiatorCount || 0} Units ({sys.pipeworkMaterial || 'Copper'})</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>UFH Loops:</Text>
                <Text style={styles.fieldValue}>{sys.underfloorHeatingLoops ? `${sys.underfloorHeatingLoops} Loops` : 'None'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 4 & 5: Pre-Flush vs Process */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>4. Pre-Flush System Condition</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Water Appearance:</Text>
              <Text style={styles.fieldValue}>{pre.preFlushWaterAppearance || 'Sludge / Magnetite'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Pre-Flush TDS / pH:</Text>
              <Text style={styles.fieldValue}>{pre.preFlushTdsPpm || 0} ppm (pH: {pre.preFlushPh || 'N/A'})</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Mains Benchmark TDS:</Text>
              <Text style={styles.fieldValue}>{pre.mainsWaterTdsPpm || 210} ppm</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Cold Spot Radiators:</Text>
              <Text style={styles.fieldValue}>{pre.radiatorColdSpotsCount || 0} Rads</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>5. Powerflushing Process</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Flushing Machine:</Text>
              <Text style={styles.fieldValue}>{flush.flushingMachineModel || 'Kamco CF90'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Chemical Cleaner / Vol:</Text>
              <Text style={styles.fieldValue}>{flush.chemicalCleanerBrand || 'Fernox F3'} ({flush.chemicalCleanerDosage || 1}L)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Reverse Flow Cycles:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{flush.flowReversalExecuted || 'Yes'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Sludge Removed:</Text>
              <Text style={styles.fieldValue}>{flush.magneticSludgeRemovedGrams || 0} grams ({flush.flushDurationHours || 0} hrs)</Text>
            </View>
          </View>
        </View>

        {/* 6: Post-Flush & Inhibitor Protection */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>6. Post-Flush Water Verification & Protection (BS 7593 Standard)</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Final Water Clarity:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{post.postFlushWaterAppearance || 'Crystal Clear'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Final System TDS:</Text>
                <Text style={styles.fieldValue}>{post.postFlushTdsPpm || 0} ppm (Diff: {post.tdsComparisonPct || 0}%)</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Final Water pH:</Text>
                <Text style={styles.fieldValue}>{post.postFlushPh || '7.6'} (Target: 7.0 - 8.5)</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>In-line Magnetic Filter:</Text>
                <Text style={styles.fieldValue}>{post.permanentMagneticFilterFitted || 'Yes'} ({post.permanentFilterMakeModel || 'MagnaClean'})</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>BS 7593 Inhibitor:</Text>
                <Text style={styles.fieldValue}>{post.chemicalInhibitorBrand || 'Fernox F1'} ({post.chemicalInhibitorDosageLtrs || 1}L)</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Test Kit Verified:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{post.inhibitorTestKitVerified || 'Yes - Verified'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Engineer Notes */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>7. Engineer Notes & Annual Re-Dose Schedule</Text>
          <Text style={{ fontSize: 6.5, color: '#334155' }}>
            {dec.engineerComments || 'Central heating system powerflushed to BS 7593:2019 standard. All radiator cold spots cleared, air vented, inhibitor added.'}
          </Text>
          <View style={[styles.fieldRow, { marginTop: 4 }]}>
            <Text style={styles.fieldLabel}>Annual Inhibitor Check Due:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold', color: '#0369a1' }]}>{dec.nextInhibitorCheckDue || '12 Months'}</Text>
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Heating Engineer</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{contractor.engineerName || 'Heating Engineer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>BS 7593 Water Treatment Stamp</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{property.flushDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Client Acceptance</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{property.clientName || 'Customer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Water clarity and balance verified</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{property.flushDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            BS 7593:2019 • Central Heating Water Treatment & Chemical Powerflush Certificate
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default CentralHeatingPowerflushCertificatePDF;
